import { useState, useCallback, useEffect, useRef, useContext } from 'react'
import { Options } from 'react-select'
import useGetProductCouriersQuery from '@src/hooks/queries/product/useGetProductCouriersQuery'
import { OptionType } from '@src/components/inputs/Select/type'
import type { ProductFormType } from '@src/type/product/form'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { blobToBase64, blobToFile, compressImage, resizeImage } from '@src/utils/image'
import _get from 'lodash/get'
import _uniqueId from 'lodash/uniqueId'
import { removeItem } from '@src/utils/utils'
import { CenterModalContext } from '@src/contexts/popup/modal/CenterModalProvider'

const useMyProductFormContext = () => {
	const bannerFileTag = useRef<HTMLInputElement>(null)
	const descFileTag = useRef<HTMLInputElement>(null)
	const sampleProofFileTag = useRef<HTMLInputElement>(null)
	const [loadingBannerFile, setLoadingBannerFile] = useState(false)
	const [loadingDescFile, setLoadingDescFile] = useState(false)
	const [loadingSampleProofFile, setLoadingSampleProofFile] = useState(false)
	// get Courier list
	const [couriers, setCouriers] = useState<Options<OptionType>>([])

	const { openCenterModal, closeCenterModal } = useContext(CenterModalContext)

	const { refetch } = useGetProductCouriersQuery()

	const loadCouriers = useCallback(async () => {
		const response = await refetch()
		if (response.data?.data?.couriers) {
			const couriersList = response.data?.data?.couriers
			const optionInit: OptionType[] = []
			const selectOptions = couriersList.reduce((acc, cur, idx) => {
				acc[idx] = { value: cur.no, label: cur.name }
				return acc
			}, optionInit)
			setCouriers(selectOptions)
		}
	}, [refetch])

	useEffect(() => {
		loadCouriers().catch(() => {})
	}, [loadCouriers])

	const {
		control: contextControl,
		getValues,
		setValue,
		formState,
		register,
		watch,
		trigger,
	} = useFormContext<ProductFormType>()

	const isOptionExist = watch('isOptionExist')
	const { isValid } = formState

	const { fields: descriptionFields, remove: removeDescByIndex } = useFieldArray({
		name: 'descriptions',
		control: contextControl,
	})
	const { remove: removeDescFileByIndex } = useFieldArray({
		name: 'descriptionFiles',
		control: contextControl,
	})
	const {
		fields: variantFields,
		append: appendVariant,
		remove: removeVariantByIndex,
	} = useFieldArray({
		name: 'productVariants',
		control: contextControl,
	})

	const {
		fields: variantOptionFields,
		replace: replaceVariantOption,
		append: appendVariantOption,
		remove: removeVariantOption,
	} = useFieldArray({
		name: 'productVariantOptions',
		control: contextControl,
	})
	const { remove: removeSku } = useFieldArray({
		name: 'productSkus',
		control: contextControl,
	})

	const execCompressImage = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target

		if (files?.[0]) {
			const compressedImage = await compressImage(files[0])
			return compressedImage
		}
		return null
	}, [])

	const selectSampleProofFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setLoadingSampleProofFile(true)
		const image = await execCompressImage(e)
		if (descFileTag?.current?.value) {
			descFileTag.current.value = ''
		}
		if (image) {
			const prevFiles = getValues('sampleProofFiles')
			setValue('sampleProofFiles', [...prevFiles, image])
			const prevDescription = getValues('sampleProofsBase64')

			blobToBase64(image)
				.then((displayFile) => {
					if (typeof displayFile === 'string') {
						if (Array.isArray(prevDescription)) {
							setValue('sampleProofsBase64', [...prevDescription, displayFile])
						} else {
							setValue('sampleProofsBase64', [displayFile])
						}
					}
					setLoadingSampleProofFile(false)
					trigger('sampleProofFiles').catch(() => {})
				})
				.catch(() => {
					setLoadingSampleProofFile(false)
					trigger('sampleProofFiles').catch(() => {})
				})
		}
	}

	const removeSampleProof = (index: number) => {
		setValue('sampleProofsBase64', removeItem(getValues('sampleProofsBase64'), index))
		setValue('sampleProofFiles', removeItem(getValues('sampleProofFiles'), index))
		trigger('sampleProofFiles').catch(() => {})
	}

	const selectDescriptionFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		setLoadingDescFile(true)
		const reader = new FileReader()

		reader.onload = async (evt) => {
			try {
				const result = evt.target?.result

				if (!result) {
					throw new Error('NOT_FOUND_RESULT')
				}

				const resizeResult = await resizeImage(String(result), 860, 5000)

				const resizedImg =
					resizeResult.size >= 3000000
						? await compressImage(resizeResult, {
								maxSizeMB: 3,
								maxWidthOrHeight: 5000,
								alwaysKeepResolution: true,
						  })
						: resizeResult

				if (descFileTag?.current?.value) {
					descFileTag.current.value = ''
				}
				const resizedImgToFile = await blobToFile(resizedImg)
				const prevFiles = getValues('descriptionFiles')
				setValue('descriptionFiles', [...prevFiles, resizedImgToFile])
				const prevDescription = getValues('descriptions')

				blobToBase64(resizedImg)
					.then((displayFile) => {
						if (typeof displayFile === 'string') {
							setValue('descriptions', [
								...prevDescription,
								{ type: 'image', base64File: displayFile },
							])
						}
						setLoadingDescFile(false)
						// Re-validate form
						trigger('descriptionFiles').catch(() => {})
					})
					.catch(() => {
						setLoadingDescFile(false)
						// Re-validate form
						trigger('descriptionFiles').catch(() => {})
					})
			} catch (error) {
				if (error instanceof Error) {
					setLoadingDescFile(false)
					if (error.message === 'MAX_HEIGHT_EXCEEDED') {
						if (descFileTag?.current?.value) {
							descFileTag.current.value = ''
						}
						alert('이미지 길이를 5000픽셀 이하로 줄여주세요.')
					}
				}
			}
		}
		const { files } = e.target
		if (files) {
			reader.readAsDataURL(files[0])
		}
		// const image = await execCompressImage(e)
		// if (descFileTag?.current?.value) {
		// 	descFileTag.current.value = ''
		// }

		// if (image) {
		// 	const prevFiles = getValues('descriptionFiles')
		// 	setValue('descriptionFiles', [...prevFiles, image])
		// 	const prevDescription = getValues('descriptions')

		// 	blobToBase64(image)
		// 		.then((displayFile) => {
		// 			if (typeof displayFile === 'string') {
		// 				setValue('descriptions', [
		// 					...prevDescription,
		// 					{ type: 'image', base64File: displayFile },
		// 				])
		// 			}
		// 			setLoadingDescFile(false)
		// 			// Re-validate form
		// 			trigger('descriptionFiles').catch(() => {})
		// 		})
		// 		.catch(() => {
		// 			setLoadingDescFile(false)
		// 			// Re-validate form
		// 			trigger('descriptionFiles').catch(() => {})
		// 		})
		// }
	}

	const removeDescription = (index: number) => {
		removeDescByIndex(index)
		removeDescFileByIndex(index)
		// Re-validate form
		trigger('descriptionFiles').catch(() => {})
	}

	const selectBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setLoadingBannerFile(true)
		const image = await execCompressImage(e)
		if (bannerFileTag?.current?.value) {
			bannerFileTag.current.value = ''
		}

		if (image) {
			setValue('bannerFile', image)
			blobToBase64(image)
				.then((displayFile) => {
					if (typeof displayFile === 'string') {
						setValue('bannerBase64', displayFile)
					}
					trigger('bannerFile').catch(() => {})
					setLoadingBannerFile(false)
				})
				.catch(() => {
					trigger('bannerFile').catch(() => {})
					setLoadingBannerFile(false)
				})
		}
	}

	const removeBanner = () => {
		setValue('bannerBase64', null)
		setValue('bannerFile', null)
		trigger('bannerFile').catch(() => {})
	}

	const appendVariantField = () => {
		const currVariant = getValues('productVariants')
		if (currVariant.length === 2) {
			return
		}
		const currDepth = currVariant.length + 1
		appendVariant({
			name: '',
			depth: currDepth,
		})
		appendVariantOption({
			name: '',
			variantDepth: currDepth,
			cno: _uniqueId('variant-option-'),
		})
	}

	const removeVariantField = (idx: number) => {
		const currVariantLength = getValues('productVariants').length
		if (currVariantLength === 1) {
			return
		}
		removeVariantByIndex(idx)

		const optionArr = getValues('productVariantOptions')
		const deleteIndexArr = []
		for (let i = 0; i < optionArr.length; i += 1) {
			const targetVariantOption = optionArr[i]

			if (targetVariantOption.variantDepth === idx + 1) {
				deleteIndexArr.push(i)
			}
		}

		if (deleteIndexArr.length > 0) {
			removeVariantOption(deleteIndexArr)
		}
	}

	const getCourierOptionsProps = ({ ...otherProps } = {}) => {
		const { errors } = formState
		const fieldErr = _get(errors, 'shipping.courier')
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			controllerProps: {
				control: contextControl,
				name: 'shipping.courier',
			},
			selectProps: {
				options: couriers,
			},
			errorMsg,
			isError: !!errorMsg,
			...otherProps,
		} as const
	}

	const getFieldProps = (fieldName: string, { ...otherProps } = {}) => {
		const { errors } = formState
		const fieldErr = _get(errors, fieldName)
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			...register(fieldName),
			errorMsg,
			isError: !!errorMsg,
			...otherProps,
		}
	}

	const getDescriptionFormProps = () => {
		const { errors } = formState
		const fieldErr = _get(errors, 'descriptionFiles')
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			selectDescriptionFile,
			descriptionFields,
			loadingDescFile,
			descFileTag,
			removeDescription,
			errorMsg,
			isError: !!errorMsg,
		}
	}

	const getBannerFormProps = () => {
		const { errors } = formState
		const fieldErr = _get(errors, 'bannerFile')
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			bannerFileTag,
			loadingBannerFile,
			bannerBase64: getValues('bannerBase64'),
			selectBannerFile,
			removeBanner,
			errorMsg,
			isError: !!errorMsg,
		}
	}

	const getSampleProofFormProps = () => {
		const { errors } = formState
		const fieldErr = _get(errors, 'sampleProofFiles')
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			removeSampleProof,
			sampleProofsBase64: getValues('sampleProofsBase64'),
			loadingSampleProofFile,
			sampleProofFileTag,
			selectSampleProofFile,
			errorMsg,
			isError: !!errorMsg,
		}
	}
	const getNormalPriceFormProps = () => {
		const stockExist = watch('stockExist')
		return {
			stockExist,
			isOptionExist,
			getFieldProps,
		}
	}

	const getProductVariantsFormProps = () => {
		const customRemoveVariant = (index: number) => {
			const currVariantLength = getValues('productVariants').length
			if (currVariantLength === 1) {
				setValue('isOptionExist', false)
				return
			}
			const targetVariant = getValues(`productVariants.${index}`)

			const skuArr = getValues('productSkus')
			const options = getValues('productVariantOptions')
			const remainOptions = options.filter((option) => {
				return option.variantDepth !== targetVariant.depth
			})

			const targetOptions = options.filter((option) => {
				return option.variantDepth === targetVariant.depth
			})
			if (index === 0) {
				const remainedVariant = getValues(`productVariants.${1}`)
				remainedVariant.depth = 1
				setValue('productVariants', [remainedVariant])

				replaceVariantOption(
					remainOptions.map((option) => {
						return { ...option, variantDepth: 1 }
					}),
				)
				setValue(
					'productSkus',
					skuArr.filter((sku, skuIndex) => {
						return skuIndex % targetOptions.length === 0
					}),
				)
			} else {
				replaceVariantOption(remainOptions)
				setValue('productSkus', skuArr.slice(0, remainOptions.length))
			}
			removeVariantField(index)
		}
		return {
			isOptionExist,
			variantFields,
			appendVariantField,
			removeVariantField: customRemoveVariant,
		}
	}

	const getProductVariantsOptionsFormProps = () => {
		const customAppend = (depth: number) => {
			const currSkuArr = watch('productSkus')
			const newTargetCno = _uniqueId('variant-option-')
			if (depth === 1) {
				const options = getValues(`productVariantOptions`)
				const horizOptions = options.filter((option) => {
					return option.variantDepth === 1
				})
				const optionlength = horizOptions.length
				const newSkuArr = []
				for (let i = 0; i < currSkuArr.length; i += 1) {
					newSkuArr.push(currSkuArr[i])

					if ((i + 1) % optionlength === 0) {
						newSkuArr.push({ cno: [], price: 0, challengePrice: 0 })
					}
				}
				setValue('productSkus', newSkuArr)
			}

			appendVariantOption({
				variantDepth: depth,
				name: '',
				cno: newTargetCno,
			})
		}
		const customRemoveVariantOption = (index: number) => {
			const options = getValues(`productVariantOptions`)
			const targetOption = getValues(`productVariantOptions.${index}`)
			const targetCno = targetOption.cno
			const targetDepth = targetOption.variantDepth
			if (
				options.filter((option) => {
					return option.variantDepth === targetDepth
				}).length > 1
			) {
				removeVariantOption(index)

				const skuArr = getValues('productSkus')

				const removeArr = []
				for (let i = 0; i < skuArr.length; i += 1) {
					if (skuArr[i].cno.includes(targetCno)) {
						removeArr.push(i)
					}
				}
				if (removeArr.length > 0) {
					removeSku(removeArr)
				}
			}
		}
		return {
			variantOptionFields,
			appendVariantOption: customAppend,
			removeVariantOption: customRemoveVariantOption,
		}
	}

	const getSkuTableProps = () => {
		return {
			variants: watch('productVariants'),
			variantOptions: watch('productVariantOptions'),
			isOptionExist,
		}
	}
	const getSkuTableItemProps = () => {
		return {
			setValue,
			getValues,
			getFieldProps,
		}
	}

	return {
		getCourierOptionsProps,
		getFieldProps,
		getDescriptionFormProps,
		getSampleProofFormProps,
		getBannerFormProps,
		getProductVariantsFormProps,
		getProductVariantsOptionsFormProps,
		getSkuTableProps,
		getSkuTableItemProps,
		getNormalPriceFormProps,
		isValid,
	}
}

export default useMyProductFormContext
